import { api } from "./axios";

export type AttendanceTrendPoint = {
  service_occurrence_id: string;
  label: string;
  service_title: string;
  service_type: string;
  starts_at: string;
  present: number;
};

export type ReportingScope = "overall" | "main" | "ministry";

export type ReportSummary = {
  scope: ReportingScope;
  ministry_id: string | null;
  unique_attendees: number;
  period_days: number;
  eligible_services: number;
  total_present: number;
  average_attendance: number;
  attendance_change: number | null;
  attendance_trend: AttendanceTrendPoint[];
};

export type AttendanceServiceDetail = {
  service_occurrence_id: string;
  service_title: string;
  service_type: string;
  starts_at: string;
  present: number;
  vs_average: number | null;
  vs_previous_service: number | null;
};

export type AttendanceHighlight = {
  service_occurrence_id: string;
  service_title: string;
  service_type: string;
  starts_at: string;
  present: number;
  vs_average: number | null;
};

export type AttendanceReport = {
  scope: ReportingScope;
  ministry_id: string | null;
  unique_attendees: number;
  period_days: number;
  service_type: string | null;

  period_start: string;
  period_end: string;

  eligible_services: number;
  total_present: number;
  average_attendance: number;
  attendance_change: number | null;

  available_service_types: string[];

  highest_attendance: AttendanceHighlight | null;
  lowest_attendance: AttendanceHighlight | null;

  attendance_trend: AttendanceTrendPoint[];
  services: AttendanceServiceDetail[];
};

export async function getReportSummary(
  churchId: string,
  days = 30,
  scope: ReportingScope = "overall",
  ministryId?: string
): Promise<ReportSummary> {
  const response = await api.get(
    `/api/churches/${churchId}/reports/summary`,
    {
      params: {
        days,
        scope,
        ministry_id: ministryId,
      },
    }
  );

  return response.data;
}

export async function getAttendanceReport(
  churchId: string,
  days = 90,
  serviceType?: string,
  scope: ReportingScope = "overall",
  ministryId?: string
): Promise<AttendanceReport> {
  const response = await api.get(
    `/api/churches/${churchId}/reports/attendance`,
    {
      params: {
        days,
        scope,
        ministry_id: ministryId,
        service_type: serviceType || undefined,
      },
    }
  );

  return response.data;
}

export const getReportingScopes = (churchId: string) => api.get<
  { ministry_id: string; name: string; is_active: boolean }[]
>(`/api/churches/${churchId}/reports/scopes`).then(response => response.data);
